'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { debounce } from 'es-toolkit';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
type AutoplayOptions = {
  delay?: number;
  stopOnInteraction?: boolean;
};
interface VerticalCarouselProps {
  children: ReactNode;
  className?: string;
  slideClassName?: string;
  loop?: boolean;
  autoplay?: boolean | AutoplayOptions;
  align?: 'start' | 'center' | 'end';
  dragFree?: boolean;
  height?: number;
}
const VerticalCarouselViewportContext = React.createContext<HTMLElement | null>(null);
const useVerticalCarouselViewport = () => {
  return React.useContext(VerticalCarouselViewportContext);
};
const VerticalCarouselBase = t0 => {
  const $ = _c(27);
  const {
    children,
    className,
    loop: t1,
    slideClassName: t2,
    autoplay: t3,
    align: t4,
    dragFree: t5
  } = t0;
  const loop = t1 === undefined ? false : t1;
  const slideClassName = t2 === undefined ? "mb-8" : t2;
  const autoplay = t3 === undefined ? false : t3;
  const align = t4 === undefined ? "start" : t4;
  const dragFree = t5 === undefined ? true : t5;
  const t6 = !loop && dragFree;
  const t7 = loop ? false : "trimSnaps";
  let t8;
  if ($[0] !== align || $[1] !== loop || $[2] !== t6 || $[3] !== t7) {
    t8 = {
      axis: "y",
      loop,
      align,
      dragFree: t6,
      containScroll: t7,
      slidesToScroll: 1
    };
    $[0] = align;
    $[1] = loop;
    $[2] = t6;
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const options = t8;
  let t9;
  if ($[5] !== autoplay) {
    t9 = autoplay ? [Autoplay(typeof autoplay === "object" ? autoplay : {
      delay: 3000,
      stopOnInteraction: false
    })] : [];
    $[5] = autoplay;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const plugins = t9;
  const [emblaRef] = useEmblaCarousel(options, plugins);
  const [viewportNode, setViewportNode] = useState(null);
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = node => {
      setViewportNode(node);
    };
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const handleViewportNodeRef = t10;
  let t11;
  if ($[8] !== className) {
    t11 = cn("mx-auto w-full overflow-hidden", className);
    $[8] = className;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("flex h-full flex-col", "[touch-action:pan-y_pinch-zoom]");
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== children || $[12] !== slideClassName) {
    let t14;
    if ($[14] !== slideClassName) {
      t14 = child => <div className={cn("mb-8", slideClassName)}>{child}</div>;
      $[14] = slideClassName;
      $[15] = t14;
    } else {
      t14 = $[15];
    }
    t13 = React.Children.map(children, t14);
    $[11] = children;
    $[12] = slideClassName;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[16] !== t13) {
    t14 = <div className={t12}>{t13}</div>;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== emblaRef || $[19] !== t14) {
    t15 = <div className="h-full overflow-hidden" ref={emblaRef}>{t14}</div>;
    $[18] = emblaRef;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t11 || $[22] !== t15) {
    t16 = <section className={t11} ref={handleViewportNodeRef}>{t15}</section>;
    $[21] = t11;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t16 || $[25] !== viewportNode) {
    t17 = <VerticalCarouselViewportContext.Provider value={viewportNode}>{t16}</VerticalCarouselViewportContext.Provider>;
    $[24] = t16;
    $[25] = viewportNode;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  return t17;
};
interface VisibilityViewPortItemProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  hiddenClassName?: string;
  visibleClassName?: string;
  transitionClassName?: string;
  threshold?: number;
}
const VisibilityViewPortItem = t0 => {
  const $ = _c(15);
  const {
    children,
    className,
    contentClassName,
    hiddenClassName: t1,
    visibleClassName: t2,
    transitionClassName: t3,
    threshold: t4
  } = t0;
  const hiddenClassName = t1 === undefined ? "opacity-0 pointer-events-none" : t1;
  const visibleClassName = t2 === undefined ? "opacity-100" : t2;
  const transitionClassName = t3 === undefined ? "transition-opacity duration-300 ease-out" : t3;
  const threshold = t4 === undefined ? 0.95 : t4;
  const viewport = useVerticalCarouselViewport();
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = debounce(visible => {
      setIsVisible(visible);
    }, 10);
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const visibilityDebounceRef = useRef(t5);
  let t6;
  let t7;
  if ($[1] !== threshold || $[2] !== viewport) {
    t6 = () => {
      const node = containerRef.current;
      if (!node) {
        return;
      }
      if (!viewport) {
        setIsVisible(true);
        return;
      }
      const visibilityDebounce = visibilityDebounceRef.current;
      const observer = new IntersectionObserver(t8 => {
        const [entry] = t8;
        if (!entry) {
          return;
        }
        visibilityDebounce(entry.isIntersecting);
      }, {
        root: viewport,
        threshold
      });
      observer.observe(node);
      return () => {
        observer.disconnect();
        visibilityDebounce.cancel?.();
      };
    };
    t7 = [threshold, viewport];
    $[1] = threshold;
    $[2] = viewport;
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  const t8 = isVisible ? visibleClassName : hiddenClassName;
  let t9;
  if ($[5] !== contentClassName || $[6] !== t8 || $[7] !== transitionClassName) {
    t9 = cn(transitionClassName, contentClassName, t8);
    $[5] = contentClassName;
    $[6] = t8;
    $[7] = transitionClassName;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const visibilityClassName = t9;
  let t10;
  if ($[9] !== children || $[10] !== visibilityClassName) {
    t10 = <div className={visibilityClassName}>{children}</div>;
    $[9] = children;
    $[10] = visibilityClassName;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== className || $[13] !== t10) {
    t11 = <div ref={containerRef} className={className}>{t10}</div>;
    $[12] = className;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  return t11;
};
type VerticalCarouselCompoundComponent = typeof VerticalCarouselBase & {
  VisibilityViewPortItem: typeof VisibilityViewPortItem;
};
const VerticalCarousel = VerticalCarouselBase as VerticalCarouselCompoundComponent;
VerticalCarousel.VisibilityViewPortItem = VisibilityViewPortItem;